import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import GenericFormDrawer from '../../../../components/GenericFormDrawer';
import Button from '../../../../components/ui/Button';
import TextInput from '../../../../components/ui/TextInput';
import GenericSelect from '../../../../components/ui/GenericSelect';
import { saveNarrative } from '../../../../services/surveyReportService';
import {
  SECTION_TITLE_KEYS, ASSESSMENTS, PRIORITIES, LIMITS,
  toForm, fromForm, emptyItem, maxItems, validateForm, themeRefOptions,
} from './narrativeEditSpecs';

// Kabuk deseni: schema=[] + children (GenericFormDrawer). onSave false → çekmece açık kalır; servis reddederse
// (409 narrativeConflict, 400 invalidNarrative) GenericFormDrawer reddi yakalar ve yine açık tutar. Toast interceptor'da.

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ItemBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 8px;
`;

const ItemHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Row3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;

const Invalid = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const Hint = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const setAt = (arr, i, v) => arr.map((x, j) => (j === i ? v : x));
const removeAt = (arr, i) => arr.filter((_, j) => j !== i);

function RemoveButton({ t, onClick }) {
  return (
    <Button variant="ghost" onClick={onClick} title={t('survey.report.narrative.removeItem')}>
      <Trash2 size={14} /> {t('survey.report.narrative.removeItem')}
    </Button>
  );
}

function AddButton({ t, onClick, disabled }) {
  return (
    <Button variant="outline" onClick={onClick} disabled={disabled}>
      <Plus size={14} /> {t('survey.report.narrative.addItem')}
    </Button>
  );
}

function StringList({ t, items, onChange, fixed, max, maxLength, labelKey }) {
  return (
    <Stack>
      {items.map((v, i) => (
        <ItemBox key={i}>
          <TextInput
            label={t(labelKey, { n: i + 1 })}
            type="textarea"
            rows={2}
            maxLength={maxLength}
            value={v}
            onChange={(e) => onChange(setAt(items, i, e.target.value))}
          />
          {!fixed && <RemoveButton t={t} onClick={() => onChange(removeAt(items, i))} />}
        </ItemBox>
      ))}
      {!fixed && <AddButton t={t} disabled={items.length >= max} onClick={() => onChange([...items, ''])} />}
    </Stack>
  );
}

export default function NarrativeSectionDrawer({ open, section, report, surveyId, t, onClose, onSaved }) {
  const narrative = report?.narrative;
  const data = report?.snapshot;
  const [form, setForm] = useState(null);
  // Final inceleme M8: istemci doğrulaması geçmezse Kaydet sessiz kalmasın — çekmecede görünür uyarı.
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (open && section && narrative) setForm(toForm(section, narrative));
    setInvalid(false);
  }, [open, section, narrative]);

  useEffect(() => { setInvalid(false); }, [form]);

  const codeOptions = useMemo(() => (data?.questions || [])
    .filter((q) => q.code)
    .map((q) => ({ id: q.code, name: `${q.code} · ${q.text}` })), [data]);
  const assessmentOptions = ASSESSMENTS.map((a) => ({ id: a, name: t(`survey.report.assessment.${a}`) }));
  const priorityOptions = PRIORITIES.map((p) => ({ id: p, name: t(`survey.report.priority.${p}`) }));
  const themeOptions = useMemo(() => themeRefOptions(narrative, data), [narrative, data]);

  if (!section || !narrative) return null;

  const handleSave = async () => {
    if (!form || !validateForm(section, form)) {
      setInvalid(true);
      return false;
    }
    await saveNarrative(surveyId, { version: report.narrativeVersion, content: fromForm(section, form, narrative) });
    onSaved?.();
    return true;
  };

  const setItems = (items) => setForm((f) => ({ ...f, items }));
  const setItem = (i, patch) => setForm((f) => ({ ...f, items: setAt(f.items, i, { ...f.items[i], ...patch }) }));
  const field = (key) => t(`survey.report.narrative.field.${key}`);
  const itemHead = (i, onRemove) => (
    <ItemHead>
      <span>{t('survey.report.narrative.field.item', { n: i + 1 })}</span>
      <RemoveButton t={t} onClick={onRemove} />
    </ItemHead>
  );
  const addItem = (disabled) => (
    <AddButton t={t} disabled={disabled} onClick={() => setItems([...(form.items || []), emptyItem(section)])} />
  );

  const renderBody = () => {
    if (!form) return null;
    switch (section) {
      case 'overall':
        return (
          <TextInput label={field('text')} type="textarea" rows={10} maxLength={LIMITS.overall}
            value={form.text} onChange={(e) => setForm({ text: e.target.value })} />
        );
      case 'keyDifferences':
        return <StringList t={t} items={form.items} onChange={setItems} max={LIMITS.keyDifferences.max}
          maxLength={LIMITS.keyDifferences.len} labelKey="survey.report.narrative.field.item" />;
      case 'synthesis':
        return <StringList t={t} items={form.items} onChange={setItems} fixed
          maxLength={LIMITS.synthesis.len} labelKey="survey.report.narrative.field.item" />;
      case 'themes':
        return (
          <Stack>
            {form.blocks.flatMap((b, bi) => b.items.map((item, ii) => (
              <ItemBox key={`${b.questionId}-${item.id}`}>
                <TextInput label={field('themeName')} maxLength={LIMITS.themes.name} value={item.name}
                  onChange={(e) => setForm((f) => ({
                    blocks: setAt(f.blocks, bi, { ...f.blocks[bi], items: setAt(f.blocks[bi].items, ii, { ...item, name: e.target.value }) }),
                  }))} />
                <TextInput label={field('themeSummary')} type="textarea" rows={4} maxLength={LIMITS.themes.summary} value={item.summary}
                  onChange={(e) => setForm((f) => ({
                    blocks: setAt(f.blocks, bi, { ...f.blocks[bi], items: setAt(f.blocks[bi].items, ii, { ...item, summary: e.target.value }) }),
                  }))} />
              </ItemBox>
            )))}
          </Stack>
        );
      case 'commentScoreLinks':
        return (
          <Stack>
            {form.items.map((l, i) => (
              <ItemBox key={i}>
                {itemHead(i, () => setItems(removeAt(form.items, i)))}
                <GenericSelect label={field('themeRef')} options={themeOptions} value={l.themeRef || null}
                  onChange={(v) => setItem(i, { themeRef: v || '' })} />
                <div>
                  <TextInput label={field('questionCodes')} value={l.codes} onChange={(e) => setItem(i, { codes: e.target.value })} />
                  <Hint>{field('codesHint')}</Hint>
                </div>
                <GenericSelect label={field('assessment')} options={assessmentOptions} value={l.assessment} clearable={false}
                  onChange={(v) => setItem(i, { assessment: v || 'consistent' })} />
              </ItemBox>
            ))}
            {addItem(form.items.length >= maxItems(section))}
          </Stack>
        );
      case 'recommendations':
        return (
          <Stack>
            {form.items.map((r, i) => (
              <ItemBox key={i}>
                {itemHead(i, () => setItems(removeAt(form.items, i)))}
                <TextInput label={field('title')} maxLength={LIMITS.recommendations.title} value={r.title}
                  onChange={(e) => setItem(i, { title: e.target.value })} />
                <div>
                  <TextInput label={field('codes')} value={r.codes} onChange={(e) => setItem(i, { codes: e.target.value })} />
                  <Hint>{field('codesHint')}</Hint>
                </div>
                <GenericSelect label={field('priority')} options={priorityOptions} value={r.priority} clearable={false}
                  onChange={(v) => setItem(i, { priority: v || 'medium' })} />
                <TextInput label={field('finding')} type="textarea" rows={3} maxLength={LIMITS.recommendations.finding}
                  value={r.finding} onChange={(e) => setItem(i, { finding: e.target.value })} />
                <StringList t={t} items={r.steps} max={LIMITS.recommendations.steps} maxLength={LIMITS.recommendations.step}
                  labelKey="survey.report.narrative.field.step" onChange={(steps) => setItem(i, { steps })} />
                <TextInput label={field('expectedOutcome')} type="textarea" rows={2} maxLength={LIMITS.recommendations.outcome}
                  value={r.expectedOutcome} onChange={(e) => setItem(i, { expectedOutcome: e.target.value })} />
              </ItemBox>
            ))}
            {addItem(form.items.length >= maxItems(section))}
          </Stack>
        );
      case 'roadmap':
        return (
          <Stack>
            <Row3>
              {form.periods.map((p, i) => (
                <ItemBox key={i}>
                  <TextInput label={field('periodLabel')} maxLength={LIMITS.roadmap.label} value={p.label}
                    onChange={(e) => setForm((f) => ({ ...f, periods: setAt(f.periods, i, { ...p, label: e.target.value }) }))} />
                  <TextInput label={field('periodSubtitle')} maxLength={LIMITS.roadmap.subtitle} value={p.subtitle}
                    onChange={(e) => setForm((f) => ({ ...f, periods: setAt(f.periods, i, { ...p, subtitle: e.target.value }) }))} />
                </ItemBox>
              ))}
            </Row3>
            {form.rows.map((r, i) => (
              <ItemBox key={i}>
                {itemHead(i, () => setForm((f) => ({ ...f, rows: removeAt(f.rows, i) })))}
                <TextInput label={field('rowArea')} maxLength={LIMITS.roadmap.cell} value={r.area}
                  onChange={(e) => setForm((f) => ({ ...f, rows: setAt(f.rows, i, { ...r, area: e.target.value }) }))} />
                <Row3>
                  {r.cells.map((c, j) => (
                    <TextInput key={j} label={t('survey.report.narrative.field.cell', { n: j + 1 })} type="textarea" rows={2}
                      maxLength={LIMITS.roadmap.cell} value={c}
                      onChange={(e) => setForm((f) => ({ ...f, rows: setAt(f.rows, i, { ...r, cells: setAt(r.cells, j, e.target.value) }) }))} />
                  ))}
                </Row3>
              </ItemBox>
            ))}
            <AddButton t={t} disabled={form.rows.length >= LIMITS.roadmap.rows}
              onClick={() => setForm((f) => ({ ...f, rows: [...f.rows, emptyItem('roadmap')] }))} />
            <Row3>
              {form.milestones.map((m, i) => (
                <TextInput key={i} label={t('survey.report.narrative.field.milestone', { n: i + 1 })} maxLength={LIMITS.roadmap.milestone}
                  value={m} onChange={(e) => setForm((f) => ({ ...f, milestones: setAt(f.milestones, i, e.target.value) }))} />
              ))}
            </Row3>
          </Stack>
        );
      case 'indicators':
        return (
          <Stack>
            {form.items.map((ind, i) => (
              <ItemBox key={i}>
                {itemHead(i, () => setItems(removeAt(form.items, i)))}
                <GenericSelect label={field('code')} options={codeOptions} value={ind.code || null}
                  onChange={(v) => setItem(i, { code: v || '' })} />
                <TextInput label={field('label')} maxLength={LIMITS.indicators.label} value={ind.label}
                  onChange={(e) => setItem(i, { label: e.target.value })} />
                <Row3>
                  <TextInput label={field('target6')} type="number" min={0} max={100} value={ind.target6}
                    onChange={(e) => setItem(i, { target6: e.target.value })} />
                  <TextInput label={field('target12')} type="number" min={0} max={100} value={ind.target12}
                    onChange={(e) => setItem(i, { target12: e.target.value })} />
                </Row3>
              </ItemBox>
            ))}
            {addItem(form.items.length >= maxItems(section))}
          </Stack>
        );
      case 'responsibilities':
        return (
          <Stack>
            {form.items.map((r, i) => (
              <ItemBox key={i}>
                {itemHead(i, () => setItems(removeAt(form.items, i)))}
                {['area', 'owner', 'frequency', 'reportsTo'].map((k) => (
                  <TextInput key={k} label={field(k)} maxLength={LIMITS.responsibilities.field} value={r[k]}
                    onChange={(e) => setItem(i, { [k]: e.target.value })} />
                ))}
              </ItemBox>
            ))}
            {addItem(form.items.length >= maxItems(section))}
          </Stack>
        );
      case 'nextSteps':
        return (
          <Stack>
            {form.items.map((s, i) => (
              <ItemBox key={i}>
                {itemHead(i, () => setItems(removeAt(form.items, i)))}
                <TextInput label={field('title')} maxLength={LIMITS.nextSteps.title} value={s.title}
                  onChange={(e) => setItem(i, { title: e.target.value })} />
                <TextInput label={field('description')} type="textarea" rows={2} maxLength={LIMITS.nextSteps.description}
                  value={s.description} onChange={(e) => setItem(i, { description: e.target.value })} />
              </ItemBox>
            ))}
            {addItem(form.items.length >= maxItems(section))}
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={t('survey.report.narrative.editTitle', { section: t(SECTION_TITLE_KEYS[section]) })}
      schema={[]}
      onSave={handleSave}
    >
      {renderBody()}
      {invalid && <Invalid role="alert" data-testid="narrative-invalid">{t('survey.report.narrative.invalid')}</Invalid>}
    </GenericFormDrawer>
  );
}
